import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FileSpreadsheet,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { workAuthService } from '@/services/workAuthService';

interface SmartsheetRecordCardProps {
  workAuthId: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

interface SmartsheetRecord {
  sheetName: string;
  rowId: number;
  rowNumber: number;
  columns: string[];
  record: Record<string, any>;
}

// Fields to highlight at the top
const PRIORITY_FIELDS = [
  'Employee ID (Dayforce)',
  'Employee Name',
  "employee's email",
  "manager's email",
  'Document Type',
  'Document Expiration Date / Section 1 Work Authoriz',
  'Citizenship Status',
  'Employee Type',
  'Position'
];

const SmartsheetRecordCard: React.FC<SmartsheetRecordCardProps> = ({ workAuthId, isOpen = true, onToggle }) => {
  const [record, setRecord] = useState<SmartsheetRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchRecord = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await workAuthService.getSmartsheetRecord(workAuthId);
      if (response.success) {
        setRecord(response.data);
        if (!response.data && response.message) {
          setError(response.message);
        }
      } else {
        setError('Failed to fetch Smartsheet record');
      }
    } catch (err) {
      console.error('Failed to load Smartsheet record:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Smartsheet record');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
  }, [workAuthId]);

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return String(value);
  };

  // Separate priority fields from others
  const getPriorityAndOtherFields = () => {
    if (!record) return { priority: [], other: [] };

    const priority: Array<{ key: string; value: any }> = [];
    const other: Array<{ key: string; value: any }> = [];

    Object.entries(record.record).forEach(([key, value]) => {
      if (PRIORITY_FIELDS.includes(key)) {
        priority.push({ key, value });
      } else {
        other.push({ key, value });
      }
    });

    // Sort priority fields by their order in PRIORITY_FIELDS
    priority.sort((a, b) => {
      return PRIORITY_FIELDS.indexOf(a.key) - PRIORITY_FIELDS.indexOf(b.key);
    });

    return { priority, other };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Smartsheet Record
            </span>
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </CardTitle>
        </CardHeader>
        {isOpen && (
          <CardContent>
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        )}
      </Card>
    );
  }

  if (error && !record) {
    return (
      <Card>
        <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Smartsheet Record
            </span>
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </CardTitle>
          {isOpen && (
            <CardDescription>
              Data from the source Smartsheet
            </CardDescription>
          )}
        </CardHeader>
        {isOpen && (
          <CardContent>
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={fetchRecord}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        )}
      </Card>
    );
  }

  if (!record) {
    return (
      <Card>
        <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Smartsheet Record
            </span>
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </CardTitle>
          {isOpen && (
            <CardDescription>
              Data from the source Smartsheet
            </CardDescription>
          )}
        </CardHeader>
        {isOpen && (
          <CardContent>
            <p className="text-sm text-muted-foreground text-center py-4">
              No matching record found in Smartsheet
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={fetchRecord}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardContent>
        )}
      </Card>
    );
  }

  const { priority, other } = getPriorityAndOtherFields();

  return (
    <Card>
      <CardHeader className={onToggle ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Smartsheet Record
              <Badge variant="secondary" className="ml-2">
                Row {record.rowNumber}
              </Badge>
            </CardTitle>
            {isOpen && (
              <CardDescription>
                {record.sheetName}
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isOpen && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); fetchRecord(); }}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
            {onToggle && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />}
          </div>
        </div>
      </CardHeader>
      {isOpen && <CardContent className="space-y-4">
        {/* Priority fields */}
        <div className="grid grid-cols-2 gap-3">
          {priority.map(({ key, value }) => (
            <div key={key} className="space-y-1">
              <p className="text-xs text-muted-foreground">{key}</p>
              <p className="text-sm font-medium">{formatValue(value)}</p>
            </div>
          ))}
        </div>

        {/* Expandable other fields */}
        {other.length > 0 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Hide {other.length} More Fields
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show {other.length} More Fields
                </>
              )}
            </Button>

            {expanded && (
              <ScrollArea className="h-[200px] rounded-md border p-3">
                <div className="grid grid-cols-2 gap-3">
                  {other.map(({ key, value }) => (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground">{key}</p>
                      <p className="text-sm">{formatValue(value)}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </>
        )}
      </CardContent>}
    </Card>
  );
};

export default SmartsheetRecordCard;
